import { Badge } from "@/components/ui/badge";
import { Create } from "@/components/admin/create";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { Edit } from "@/components/admin/edit";
import { List } from "@/components/admin/list";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextArrayInput } from "@/components/admin/text-array-input";
import { TextInput } from "@/components/admin/text-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { useRecordContext } from "ra-core";

import { TopToolbar } from "../layout/TopToolbar";
import { MARKETING_PATHS } from "./paths";

/**
 * Content library — the reusable email / script / LinkedIn / note bodies that
 * sequence steps send, keyed by name rather than copy-pasted into each step.
 *
 * WHY RETIRE, NEVER DELETE
 * ------------------------
 * A sequence step or a piece of engagement history can reference a row here
 * long after it stops being the current version of anything. Deleting a row
 * destroys the answer to "which version actually went out?" -- exactly the
 * failure this fork keeps avoiding for doors and mailings. `is_active` is the
 * only retirement path. There is deliberately no delete button on this
 * screen's Edit page (see the `actions` override below): the upstream default
 * would have offered one, quietly, the same way DataTable's default bulk
 * actions once offered to delete doors.
 *
 * WHY MERGE FIELDS ARE SPELLED OUT HERE
 * --------------------------------------
 * `{{first_name}}`, `{{last_name}}`, `{{company}}` and `{{title}}` only work if
 * an author knows they exist and knows the exact spelling -- render logic
 * lives in ./templates.ts, which turns an unresolved field into a visible
 * `[[field?]]` marker rather than a blank. But a marker caught at send time is
 * a near-miss, not a save; the point of naming the fields here is to stop the
 * typo before it is ever saved.
 */

export type ContentAsset = {
  id: number;
  name: string;
  kind: "email" | "call_script" | "linkedin" | "note" | "other";
  subject: string | null;
  body: string;
  /** Which compiled shell this is sent in. See SHELL_CHOICES. */
  shell: "plain" | "branded";
  tags: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

/**
 * Which compiled wrapper this content is sent in.
 *
 * A PROPERTY OF THE CONTENT, NOT OF THE SEND. "Trade show follow-up" is
 * always personal and "Quarterly product news" is always a broadcast, so the
 * wrapper is stored here rather than chosen per message -- a caller that
 * could choose would only ever choose wrong once, silently.
 *
 * IT DEFAULTS TO PLAIN, and that default is the safe one in the direction
 * that matters: a personal note in a newsletter wrapper reads as a mistake,
 * a newsletter sent plain merely reads as understated.
 *
 * Only consulted when the asset is EMAILED. A call script carries the default
 * and nothing reads it.
 */
const SHELL_CHOICES = [
  { id: "plain", name: "Plain — a one-to-one email" },
  { id: "branded", name: "Branded — a broadcast, with the masthead" },
];

const KIND_CHOICES = [
  { id: "email", name: "Email" },
  { id: "call_script", name: "Call script" },
  { id: "linkedin", name: "LinkedIn" },
  { id: "note", name: "Note" },
  { id: "other", name: "Other" },
];

const KIND_LABEL: Record<ContentAsset["kind"], string> = {
  email: "Email",
  call_script: "Call script",
  linkedin: "LinkedIn",
  note: "Note",
  other: "Other",
};

/** The fields renderTemplate() understands. Kept in one place so the list
 *  shown to authors can never drift from what actually resolves. */
const MERGE_FIELDS = ["first_name", "last_name", "company", "title"] as const;

const MergeFieldHelp = () => (
  <div className="text-xs text-muted-foreground border rounded-md p-3 space-y-1.5 max-w-lg">
    <p className="font-medium text-foreground">Merge fields available</p>
    <p>
      Use these in the subject or body — a field that has no value at send time
      renders as a visible marker like{" "}
      <code className="font-mono">[[company?]]</code> rather than a silent
      blank.
    </p>
    <div className="flex flex-wrap gap-1.5 pt-1">
      {MERGE_FIELDS.map((f) => (
        <Badge key={f} variant="outline" className="font-mono">
          {`{{${f}}}`}
        </Badge>
      ))}
    </div>
  </div>
);

const KindField = () => {
  const record = useRecordContext<ContentAsset>();
  if (!record) return null;
  return <Badge variant="outline">{KIND_LABEL[record.kind]}</Badge>;
};

const ActiveField = () => {
  const record = useRecordContext<ContentAsset>();
  if (!record) return null;
  return (
    <Badge variant={record.is_active ? "default" : "outline"}>
      {record.is_active ? "active" : "retired"}
    </Badge>
  );
};

const TagsField = () => {
  const record = useRecordContext<ContentAsset>();
  if (!record?.tags?.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {record.tags.map((t) => (
        <Badge key={t} variant="secondary" className="font-normal">
          {t}
        </Badge>
      ))}
    </div>
  );
};

/**
 * Active-by-default, with a one-click way to see what has been retired.
 *
 * `filterDefaultValues` rather than a permanent `filter`: a permanent filter
 * cannot be lifted from this screen at all, and "show me the retired ones
 * too" is a legitimate question -- the same reasoning the Doors list uses for
 * suppressed/retired doors.
 */
const ContentLibraryActions = () => {
  return (
    <TopToolbar>
      {/*
        w-auto, because ToggleFilterButton hard-codes `w-full`.
        That is a SIDEBAR assumption baked into a shared component: every other
        caller (DoorListFilter, ContactListFilter, CompanyListFilter) puts it in
        a narrow vertical column where filling the width is what you want.
        This is the only screen using it in a horizontal toolbar, and TopToolbar
        is `flex-auto`, so each button stretched across the whole row and landed
        on top of the page title -- the heading read "Conte**Retired**".
      */}
      <ToggleFilterButton
        className="w-auto"
        label="Active"
        value={{ is_active: true }}
      />
      <ToggleFilterButton
        className="w-auto"
        label="Retired"
        value={{ is_active: false }}
      />
      <CreateButton />
    </TopToolbar>
  );
};

export const ContentLibraryList = () => (
  <List
    resource="content_assets"
    title="Content library"
    actions={<ContentLibraryActions />}
    filterDefaultValues={{ is_active: true }}
    sort={{ field: "name", order: "ASC" }}
    perPage={25}
  >
    <div className="pb-3">
      <p className="text-sm text-muted-foreground max-w-2xl">
        Reusable email, call script, LinkedIn and note bodies that sequence
        steps send by name. Retire a version with the Active switch on its edit
        page rather than deleting it — sequence steps and engagement history
        keep referencing old versions, and deleting one destroys the answer to
        "which version went out?".
      </p>
    </div>
    <DataTable rowClick="edit">
      <DataTable.Col source="name" label="Name" />
      <DataTable.Col label="Kind">
        <KindField />
      </DataTable.Col>
      <DataTable.Col label="Sent as">
        <ShellField />
      </DataTable.Col>
      <DataTable.Col source="subject" label="Subject" />
      <DataTable.Col label="Tags">
        <TagsField />
      </DataTable.Col>
      <DataTable.Col label="Status">
        <ActiveField />
      </DataTable.Col>
    </DataTable>
  </List>
);

/**
 * The wrapper, in the list, so it can be scanned rather than opened.
 *
 * A branded asset is the exception and should look like one, so it is the
 * only value that gets a badge; plain says so quietly. Anything else means
 * the database grew a third shell the app has not been told about, and it
 * says the word back rather than rendering an empty cell.
 */
const ShellField = () => {
  const record = useRecordContext<ContentAsset>();
  if (!record) return null;
  if (record.shell === "branded")
    return <Badge variant="default">Branded</Badge>;
  if (record.shell === "plain")
    return <span className="text-muted-foreground text-sm">Plain</span>;
  return (
    <span className="text-destructive text-sm">
      {record.shell ?? "none"} — unknown
    </span>
  );
};

const ContentAssetForm = () => (
  <SimpleForm>
    <TextInput source="name" label="Name" />
    <SelectInput
      source="kind"
      label="Kind"
      choices={KIND_CHOICES}
      defaultValue="email"
    />
    <SelectInput
      source="shell"
      label="Sent as"
      choices={SHELL_CHOICES}
      defaultValue="plain"
      helperText="Plain looks like a message a person typed; branded carries the masthead and reads as a newsletter. Only used when this is emailed."
    />
    <TextInput source="subject" label="Subject (optional)" />
    <MergeFieldHelp />
    <TextInput source="body" label="Body" multiline rows={8} />
    <TextArrayInput source="tags" label="Tags" placeholder="Add a tag…" />
    <BooleanInput
      source="is_active"
      label="Active"
      defaultValue={true}
      helperText="Turn off to retire this version. It stops being offered for new sends but stays readable wherever it was already used."
    />
  </SimpleForm>
);

/**
 * Saving returns to the library UNDER CONTACTS, not to `redirect="list"`.
 *
 * "list" means the `content_assets` Resource's own route, `/content_assets`,
 * which renders this same library with no Contacts sub-nav above it. So the
 * round trip from /contacts/content -> Write a template -> Save ended on a
 * third URL showing an identical screen, minus the navigation that got you
 * there -- the reader has no way to tell they have been moved, only that the
 * tabs vanished.
 */
export const ContentLibraryCreate = () => (
  <Create resource="content_assets" redirect={MARKETING_PATHS.contactContent}>
    <ContentAssetForm />
  </Create>
);

/**
 * No DeleteButton in `actions`: the upstream Edit default renders one
 * unconditionally, which is exactly the "retire via delete" path this
 * resource must not offer. Only a ShowButton-free header remains — retiring
 * is the Active switch above, on the same page.
 */
export const ContentLibraryEdit = () => (
  <Edit
    resource="content_assets"
    actions={<></>}
    redirect={MARKETING_PATHS.contactContent}
  >
    <ContentAssetForm />
  </Edit>
);

/**
 * One default export, so CRM.tsx's injection is a single `{...contentLibrary}`
 * spread rather than three separate named imports -- matching the shape
 * upstream already uses for `companies` and `sales`.
 */
export default {
  list: ContentLibraryList,
  create: ContentLibraryCreate,
  edit: ContentLibraryEdit,
};
